import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  current,
  nanoid,
  isAnyOf,
} from "@reduxjs/toolkit";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { closestTo, format, isFuture, isValid } from "date-fns";
import { RootState } from "../../app/store";
import isEmpty from "../../common/helpers/isEmpty";
import { SalaryType } from "../orders/types/salaryType";
import { WorkPeriodType } from "../orders/types/workPeriodType";
import { WorkTimeType } from "../orders/types/workTimeType";
import { ExpenseForEmployment } from "../talentProfile/Employment/EmploymentAdditionalInfo/types";
import { EventPayload } from "./../../common/types/Event";
import {
  AccumDeltaAdded,
  accumulationsForContinuousEmployment,
  AccumulationsForContinuousEmploymentFilters,
  accumulationsForEmployment,
  AccumulationsForEmploymentFilters,
  AccumulationsForEmploymentResponse,
  AddAccumDelta,
  addAccumDelta,
  changeEmpAnnualLeavePaymentRule,
  changeEmpAnnualHolidayAccumulationRule,
  changeEmploymentAgreedHours,
  changeEmpOtherAgreedInformation,
  changePeriodStart,
  addEmploymentPeriodStartSetting,
  editEmploymentPeriodStartSetting,
  removeEmploymentPeriodStartSetting,
  AddEmploymentPeriodStartSetting,
  EditEmploymentPeriodStartSetting,
  RemoveEmploymentPeriodStartSetting,
  EmploymentPeriodStartSettingEvent,
  changeWorkTimeBankSettings,
  ClearEmpAttrSalaryParams,
  ClearEmpAttrSalaryResponse,
  clearEmploymentAttributesSalaryDate,
  DelAccumDelta,
  delAccumDelta,
  EditEmploymentAgreedHours,
  EditEmploymentAnnualLeavePaymentRule,
  EditEmploymentOtherAgreedInformation,
  EditEmploymentPeriodStart,
  EditEmpSalary,
  editEmpSalary,
  EditWorkTimeBankSettings,
  Employment,
  EmploymentAgreedHours,
  EmploymentAnnualLeavePaymentRuleChanged,
  EmploymentAttribute,
  EmploymentAttributeFilters,
  EmploymentAttributesEditingFilters,
  EmploymentAttributesForEditing,
  employmentAttributesForEditing,
  employmentAttributesOverview,
  EmploymentEndChanged,
  EmploymentOtherAgreedInformationChanged,
  EmploymentPeriodStartChanged,
  EmpSalaryChanged,
  EndEmpAttrSalaryParams,
  EndEmpAttrSalaryResponse,
  endEmploymentAttributesSalaryDate,
  expensesForEmployment,
  ExpensesForEmploymentFilters,
  getEmploymentById,
  setEmpAttrSalary,
  SetEmpAttrSalaryParams,
  SetEmpAttrSalaryResponse,
  SetEmploymentEnd,
  setEmploymentEnd,
  WorkTimeBankSettingsResponse,
  EmploymentAnnualHolidayAccumulationRuleChanged,
  EditEmploymentAnnualHolidayAccumulationRule,
  getAccumulationsForTalent,
  TalentAccumulationsResponse,
  EmploymentTargetedShiftSpec,
  getEmploymentTargetedShiftSpec,
  doSetShiftTargetingRules,
  SetShiftTargetingRules,
  updateTargetedShiftsValidationAssignment,
  SetTargetedShiftsValidationAssignment,
  changeFlexibleHoursSettings,
  getFringeBenefitTypes,
  replaceFringeBenefits,
} from "./employmentApi";
import {
  applyTesSalaryTableCellToEmploymentAttributes,
  ApplyTesSalaryTableCellParams,
} from "../tesSalaryTables/tesSalaryTablesApi";
import type { Draft, PayloadAction } from "@reduxjs/toolkit";
import { TargetedShiftSpec } from "./targetedShiftSalaries/targetedShiftSalariesApi";
import { normalizeSalaryRows } from "./targetedShiftSalaries/helpers";
import {
  saveTargetedShiftSalaryRow,
  removeTargetedShiftSalaryRow,
} from "./targetedShiftSalaries/targetedShiftSalariesActions";
import {
  ChangeFringeBenefits,
  FringeBenefitApiType,
  FringeBenefitType,
} from "../talentProfile/Employment/EmploymentAdditionalInfo/FringeBenefits/types";
import {
  employmentDateInHelsinki,
  periodStartBaseline,
  resolvePeriodStartDate,
} from "./periodStartSettings";

type Status = "idle" | "pending" | "succeeded" | "failed";

export type SliceState = {
  attributes: {
    status: Status;
    ids: [];
    entities: {};
    error?: string;
    supportedZones?: { [tesId: string]: string[] };
  };
  attributesForEditing: EmploymentAttributesForEditingState;
  additionalInfo: Employment;
  status: Status;
};

type EmploymentAttributesForEditingState = {
  status: Status;
  attributes: EmploymentAttributesForEditing;
};

export type EmpAttrExtraParams = {
  id?: string;
  from: string;
  totalSalary?: string;
  zonedSalaryTotal?: { [zone: string]: string };
};

export type SetEmpAttrSalaryReducerParams = SetEmpAttrSalaryParams &
  EmpAttrExtraParams;

const initialAttributesForEditingState: EmploymentAttributesForEditingState = {
  status: "idle" as Status,
  attributes: {
    version: 0,
    workTitleId: "",
    tesId: "",
    salaryComponents: [],
    salaryPerDate: [],
    validFrom: null,
  },
};

const employmentAttributesAdapter = createEntityAdapter<EmploymentAttribute>();

export const fetchEmploymentById = createAsyncThunk<
  Employment,
  string,
  { state: RootState }
>(
  "employment/fetchEmploymentById",
  async (employmentId: string, { requestId }) => {
    const res = await getEmploymentById(employmentId, { requestId });
    return res.data;
  },
);

export const fetchEmploymentAttributesOverview = createAsyncThunk<
  {
    attributes: EmploymentAttribute[];
    supportedZones?: { [tesId: string]: string[] };
  },
  EmploymentAttributeFilters,
  { state: RootState }
>(
  "employment/fetchEmploymentAttributesOverView",
  async (params: { talentId: string; date: string }, { requestId }) => {
    const response = await employmentAttributesOverview(params, { requestId });

    return {
      attributes: response.rows.map((item) => ({ ...item, id: nanoid() })),
      supportedZones: response.supportedZones,
    };
  },
);

export const fetchEmploymentAttributesForEditing = createAsyncThunk<
  EmploymentAttributesForEditing,
  EmploymentAttributesEditingFilters,
  { state: RootState }
>(
  "employment/fetchEmploymentAttributesForEditing",
  async (
    params: {
      talentId: string;
      workTitleId: string;
      tesId: string;
    },
    { requestId },
  ): Promise<EmploymentAttributesForEditing> => {
    return await employmentAttributesForEditing(params, { requestId });
  },
);

export const setNewEmpAttrSalary = createAsyncThunk<
  SetEmpAttrSalaryResponse,
  SetEmpAttrSalaryReducerParams,
  { state: RootState }
>(
  "employment/setEmpAttrSalary",
  async (params, { requestId }): Promise<SetEmpAttrSalaryResponse> => {
    const { id, totalSalary, ...rest } = params;

    return await setEmpAttrSalary(rest, { requestId });
  },
);

export const applyTesSalaryTableCell = createAsyncThunk<
  SetEmpAttrSalaryResponse,
  ApplyTesSalaryTableCellParams,
  { state: RootState }
>("employment/applyTesSalaryTableCell", async (params, { requestId }) => {
  return await applyTesSalaryTableCellToEmploymentAttributes(params, {
    requestId,
  });
});

export const clearEmploymentAttributesSalary = createAsyncThunk<
  EventPayload<ClearEmpAttrSalaryResponse>,
  ClearEmpAttrSalaryParams,
  { state: RootState }
>(
  "employment/clrEmpAttrSalary",
  async (
    params,
    { requestId },
  ): Promise<EventPayload<ClearEmpAttrSalaryResponse>> =>
    await clearEmploymentAttributesSalaryDate(params, { requestId }),
);

export const endEmploymentAttributesSalary = createAsyncThunk<
  EventPayload<EndEmpAttrSalaryResponse>,
  EndEmpAttrSalaryParams,
  { state: RootState }
>(
  "employment/endEmpAttrSalary",
  async (
    params,
    { requestId },
  ): Promise<EventPayload<EndEmpAttrSalaryResponse>> =>
    await endEmploymentAttributesSalaryDate(params, { requestId }),
);

const isCurrentSalary = (allDates: Date[], date: string): boolean => {
  const filterFutureDates = allDates.filter(
    (date) => !isFuture(new Date(date)),
  );

  const currentSalary = closestTo(new Date(), filterFutureDates);

  return Boolean(
    currentSalary &&
      isValid(currentSalary) &&
      format(currentSalary, "yyyy-MM-dd") === date,
  );
};

export const fetchAccumulationsForContinuousEmployment = createAsyncThunk<
  AccumulationsForEmploymentResponse,
  AccumulationsForContinuousEmploymentFilters,
  { state: RootState }
>(
  "employment/fetchAccumulationsForContinuousEmployment",
  async (
    filters: AccumulationsForContinuousEmploymentFilters,
    { requestId },
  ): Promise<AccumulationsForEmploymentResponse> =>
    await accumulationsForContinuousEmployment(filters, { requestId }),
);

export const editEmploymentSalary = createAsyncThunk<
  EventPayload<EmpSalaryChanged>[],
  EditEmpSalary,
  { state: RootState }
>(
  "employment/editEmploymentSalary",
  async (
    data: EditEmpSalary,
    { requestId },
  ): Promise<EventPayload<EmpSalaryChanged>[]> =>
    await editEmpSalary(data, { requestId }),
);

export const addEmploymentSalary = createAsyncThunk<
  EventPayload<EmpSalaryChanged>[],
  EditEmpSalary,
  { state: RootState }
>(
  "employment/addEmploymentSalary",
  async (
    data: EditEmpSalary,
    { requestId },
  ): Promise<EventPayload<EmpSalaryChanged>[]> =>
    await editEmpSalary(data, { requestId }),
);

export const fetchAccumulationsForEmployment = createAsyncThunk<
  AccumulationsForEmploymentResponse,
  AccumulationsForEmploymentFilters,
  { state: RootState }
>(
  "employment/fetchAccumulationsForEmployment",
  async (
    filters: AccumulationsForEmploymentFilters,
    { requestId },
  ): Promise<AccumulationsForEmploymentResponse> =>
    await accumulationsForEmployment(filters, { requestId }),
);

export const fetchAccumulationsForTalent = createQueryAsyncThunk<
  TalentAccumulationsResponse,
  string
>("employment/fetchAccumulationsForTalent", getAccumulationsForTalent);

export const addAccumulationDelta = createAsyncThunk<
  EventPayload<AccumDeltaAdded>[],
  AddAccumDelta,
  { state: RootState }
>(
  "employment/addAccumDelta",
  async (params, { requestId }): Promise<EventPayload<AccumDeltaAdded>[]> =>
    await addAccumDelta(params, { requestId }),
);

/**
 * a slightly changed version of addAccumulationDelta, just adds something to requestId to distinguish
 */
export const addMassAccumulationDelta = createAsyncThunk<
  EventPayload<AccumDeltaAdded>[],
  AddAccumDelta,
  { state: RootState }
>(
  "employment/addAccumDelta",
  async (params, { requestId }): Promise<EventPayload<AccumDeltaAdded>[]> =>
    await addAccumDelta(params, { requestId }),
  {
    idGenerator: (aad: AddAccumDelta): string =>
      nanoid() +
      aad.bulkUpdateId
        ?.split(";")
        .filter((s) => !s.startsWith("target"))
        .map((p) => p.split("=")[1])
        .flat()
        .join("-"),
  },
);

export const delAccumulationDelta = createAsyncThunk<
  EventPayload<any>,
  DelAccumDelta,
  { state: RootState }
>(
  "employment/delAccumDelta",
  async (params, { requestId }): Promise<EventPayload<any>> =>
    await delAccumDelta(params, { requestId }),
);

export const fetchEmploymentExpenses = createAsyncThunk<
  ExpenseForEmployment[],
  ExpensesForEmploymentFilters,
  { state: RootState }
>(
  "employment/fetchEmploymentExpenses",
  async (
    filters: ExpensesForEmploymentFilters,
    { requestId },
  ): Promise<ExpenseForEmployment[]> =>
    await expensesForEmployment(filters, { requestId }),
);

export const addPeriodStartSetting = createCommandAsyncThunk<
  AddEmploymentPeriodStartSetting,
  EmploymentPeriodStartSettingEvent
>("employment/addPeriodStartSetting", addEmploymentPeriodStartSetting);

export const editPeriodStartSetting = createCommandAsyncThunk<
  EditEmploymentPeriodStartSetting,
  EmploymentPeriodStartSettingEvent
>("employment/editPeriodStartSetting", editEmploymentPeriodStartSetting);

export const removePeriodStartSetting = createCommandAsyncThunk<
  RemoveEmploymentPeriodStartSetting,
  EmploymentPeriodStartSettingEvent
>("employment/removePeriodStartSetting", removeEmploymentPeriodStartSetting);

export const changePeriodStartDate = createAsyncThunk<
  EventPayload<EmploymentPeriodStartChanged>[],
  EditEmploymentPeriodStart,
  { state: RootState }
>(
  "employment/changePeriodStartDate",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<EmploymentPeriodStartChanged>[]> =>
    await changePeriodStart(data, { requestId }),
);

export const setContinousEmploymentId = createAction(
  "employment/setContinuousEmploymentId",
  (continuousEmploymentId: string) => {
    return {
      payload: {
        continuousEmploymentId,
      },
    };
  },
);

export const endEmployment = createAsyncThunk<
  EventPayload<EmploymentEndChanged>[],
  SetEmploymentEnd,
  { state: RootState }
>(
  "employment/endEmployment",
  async (data, { requestId }): Promise<EventPayload<EmploymentEndChanged>[]> =>
    await setEmploymentEnd(data, { requestId }),
);

export const editEmpOtherAgreedInformation = createAsyncThunk<
  EventPayload<EmploymentOtherAgreedInformationChanged>[],
  EditEmploymentOtherAgreedInformation,
  { state: RootState }
>(
  "employment/editEmpOtherAgreedInformation",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<EmploymentOtherAgreedInformationChanged>[]> =>
    await changeEmpOtherAgreedInformation(data, { requestId }),
);

export const editEmpAnnualLeavePaymentRule = createAsyncThunk<
  EventPayload<EmploymentAnnualLeavePaymentRuleChanged>[],
  EditEmploymentAnnualLeavePaymentRule,
  { state: RootState }
>(
  "employment/editEmpAnnualLeavePaymentRule",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<EmploymentAnnualLeavePaymentRuleChanged>[]> =>
    await changeEmpAnnualLeavePaymentRule(data, { requestId }),
);

export const editEmpAnnualHolidayAccumulationRule = createAsyncThunk<
  EventPayload<EmploymentAnnualHolidayAccumulationRuleChanged>[],
  EditEmploymentAnnualHolidayAccumulationRule,
  { state: RootState }
>(
  "employment/editEmpAnnualHolidayAccumulationRule",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<EmploymentAnnualHolidayAccumulationRuleChanged>[]> =>
    await changeEmpAnnualHolidayAccumulationRule(data, { requestId }),
);

export const updateEmploymentAgreedHours = createAsyncThunk<
  EventPayload<EmploymentAgreedHours>[],
  EditEmploymentAgreedHours,
  { state: RootState }
>("employment/editEmploymentAgreedHours", changeEmploymentAgreedHours);

export const updateEmploymentWorkTimeBankSettings = createAsyncThunk<
  EventPayload<WorkTimeBankSettingsResponse>[],
  EditWorkTimeBankSettings,
  { state: RootState }
>(
  "employment/updateEmploymentWorkTimeBankSettings",
  changeWorkTimeBankSettings,
);

export const updateEmploymentFlexibleHoursSettings = createAsyncThunk<
  EventPayload<WorkTimeBankSettingsResponse>[],
  EditWorkTimeBankSettings,
  { state: RootState }
>(
  "employment/updateEmploymentFlexibleHoursSettings",
  changeFlexibleHoursSettings,
);

const handleEmpAttrSalaryFulfilled = (
  state: Draft<SliceState>,
  action: PayloadAction<
    SetEmpAttrSalaryResponse,
    string,
    { arg: EmpAttrExtraParams }
  >,
) => {
  // console.log("handleEmpAttrSalaryFulfilled.succeeded", action)
  const { version } = action.payload;
  const { workTitleId, tesId } = action.payload.payload;
  const { id, from } = action.meta.arg;

  const newSalary = {
    workTitleId,
    tesId,
    version,
    totalSalary: action.meta.arg.totalSalary as string,
    ...(action.meta.arg.zonedSalaryTotal && {
      zonedSalary: action.meta.arg.zonedSalaryTotal,
    }),
  };

  // Update supportedZones if TES has zoned salary
  if (action.meta.arg.zonedSalaryTotal) {
    if (!state.attributes.supportedZones) {
      state.attributes.supportedZones = {};
    }

    // Add zones that are present in zonedSalaryTotal
    const zones = Object.keys(action.meta.arg.zonedSalaryTotal);
    if (zones.length > 0) {
      state.attributes.supportedZones[tesId] = zones;
    }
  }

  if (!id) {
    // add new salary

    // check if the added salary with given info already exists
    const existingSalary = employmentAttributesAdapter
      .getSelectors()
      .selectAll(state.attributes)
      .find((item) => item.workTitleId === workTitleId && item.tesId === tesId);

    const allSalaryDates =
      state.attributesForEditing.attributes.salaryPerDate.map(
        (salary) => new Date(salary.date),
      );

    if (existingSalary) {
      // if the modified salary is a currently valid salary,
      // update the total salary in the list displayed in the talent profile
      if (isCurrentSalary(allSalaryDates, from)) {
        employmentAttributesAdapter.updateOne(state.attributes, {
          id: existingSalary.id,
          changes: {
            totalSalary: action.meta.arg.totalSalary as string,
            version,
            ...(action.meta.arg.zonedSalaryTotal && {
              zonedSalary: action.meta.arg.zonedSalaryTotal,
            }),
          },
        });
      }
    } else {
      // add new salary to the list displayed in the talent profile
      employmentAttributesAdapter.setOne(state.attributes, {
        ...newSalary,
        id: nanoid(),
      });
    }
  } else {
    const allSalaryDates =
      state.attributesForEditing.attributes.salaryPerDate.map(
        (salary) => new Date(salary.date),
      );

    // if the modified salary is a currently valid salary,
    // update the total salary in the list displayed in the talent profile
    if (isCurrentSalary(allSalaryDates, from)) {
      employmentAttributesAdapter.updateOne(state.attributes, {
        id,
        changes: {
          totalSalary: newSalary.totalSalary,
          version,
          ...(newSalary.zonedSalary && {
            zonedSalary: newSalary.zonedSalary,
          }),
        },
      });
    }
  }
  state.attributes.status = "succeeded";
};

export const fetchEmploymentTargetedShiftSpec = createQueryAsyncThunk<
  EmploymentTargetedShiftSpec,
  string
>(
  "employment/fetchEmploymentTargetedShiftSpec",
  getEmploymentTargetedShiftSpec,
);

export const setShiftTargetingRules = createAsyncThunk<
  EventPayload<SetShiftTargetingRules>[],
  SetShiftTargetingRules
>("employment/setShiftTargetingRules", doSetShiftTargetingRules);

export const disableTargetedShifts = createAsyncThunk<
  EventPayload<SetShiftTargetingRules>[],
  { employmentId: string; version: number }
>(
  "employment/disableTargetedShifts",
  ({ employmentId, version }, { requestId }) =>
    doSetShiftTargetingRules(
      { employmentId, version, rules: [] },
      { requestId },
    ),
);

export const selectTargetedShiftValidationAssignment = createAsyncThunk<
  EventPayload<SetTargetedShiftsValidationAssignment>[],
  SetTargetedShiftsValidationAssignment
>(
  "employment/updateTargetedShiftsValidationAssignment",
  updateTargetedShiftsValidationAssignment,
);

export const fetchFringeBenefitTypes = createQueryAsyncThunk<
  FringeBenefitType[],
  {}
>("employment/fetchFringeBenefitTypes", getFringeBenefitTypes);

export const saveFringeBenefits = createAsyncThunk<
  EventPayload<{ fringeBenefits: FringeBenefitApiType[] }>[],
  ChangeFringeBenefits
>("employment/saveFringeBenefits", replaceFringeBenefits);

export const employmentSlice = createSlice({
  name: "employment",
  initialState: {
    attributes: employmentAttributesAdapter.getInitialState({
      status: "idle",
    }),
    attributesForEditing: initialAttributesForEditingState,
    additionalInfo: {},
  } as SliceState,
  reducers: {
    updateTargetedShiftSpec: (
      state,
      action: PayloadAction<TargetedShiftSpec>,
    ) => {
      if (state.additionalInfo) {
        state.additionalInfo.targetedShiftSpec = action.payload;
      }
    },
    // TODO: change state type to optional and handle state reset better
    resetEmploymentAdditionalInfo: (state) => {
      state.additionalInfo = {
        employerId: "",
        talentId: "",
        continuousEmploymentId: "",
        contEmpId: "",
        employmentId: "",
        tesId: "",
        isShift: false,
        isCanceled: false,
        workAgreementDocumentId: null,
        details: {
          version: 0,
          agreedStart: "",
          agreedEnd: "",
          workTitleId: "",
          employmentType: "",
          workTimeType: WorkTimeType.Default,
          paymentPeriodClassId: "",
          annualHolidayAccumulationRule: "",
          annualLeavePaymentRule: "",
          tempEmploymentReason: "",
          employerName: "",
          isAveragingPeriodInUse: false,
          periodStartDate: null,
          periodStartBaseline: null,
          periodStartSettings: [],
          periodType: WorkPeriodType.Weekly,
          salaryType: SalaryType.Default,
          salary: {
            ranges: [],
          },
          wageSystem: SalaryType.Default,
          otherAgreedInformation: "",
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmploymentById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchEmploymentById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchEmploymentById.fulfilled, (state, action) => {
      state.status = "succeeded";
      if (
        state.additionalInfo.employmentId === action.payload.employmentId &&
        state.additionalInfo.details?.version > action.payload.details.version
      )
        return;
      state.additionalInfo = {
        ...action.payload,
        // from the backend the continuousEmploymentId comes in fact as contEmpId
        continuousEmploymentId:
          action.payload.contEmpId ||
          action.payload.continuousEmploymentId ||
          state.additionalInfo.continuousEmploymentId,
      };
    });
    builder.addCase(addEmploymentSalary.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addEmploymentSalary.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addEmploymentSalary.fulfilled, (state, action) => {
      action.payload.forEach((event) => {
        if (event.name === "empSalChg") {
          const currentSalaries = state.additionalInfo.details.salary.ranges;
          if (!isEmpty(event.payload.addsAndMods)) {
            currentSalaries.push(event.payload.addsAndMods[0]);
            state.additionalInfo.details.version = event.version;
          }
        }
      });

      state.status = "succeeded";
    });
    builder.addCase(changePeriodStartDate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(changePeriodStartDate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(changePeriodStartDate.fulfilled, (state, action) => {
      action.payload.forEach((event) => {
        if (event.name === "empPerChg") {
          state.additionalInfo.details.periodStartDate =
            event.payload.periodStart;
          state.additionalInfo.details.periodStartBaseline =
            event.payload.periodStart;
          state.additionalInfo.details.version = event.version;
        }
      });

      state.status = "succeeded";
    });
    builder.addCase(editEmploymentSalary.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(editEmploymentSalary.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(editEmploymentSalary.fulfilled, (state, action) => {
      action.payload.forEach((event) => {
        if (event.name === "empSalChg") {
          // add new salary
          if (!isEmpty(event.payload.addsAndMods)) {
            const salaryRangeIndex =
              state.additionalInfo.details.salary.ranges.findIndex(
                (range) => range.from === event.payload.addsAndMods[0].from,
              );

            if (salaryRangeIndex !== -1) {
              state.additionalInfo.details.salary.ranges[salaryRangeIndex] =
                event.payload.addsAndMods[0];
              state.additionalInfo.details.version = event.version;
            }
          }

          // remove salary
          if (!isEmpty(event.payload.removes)) {
            const salaryRangeIndex =
              state.additionalInfo.details.salary.ranges.findIndex(
                (range) => range.from === event.payload.removes[0],
              );

            if (salaryRangeIndex !== -1) {
              state.additionalInfo.details.salary.ranges.splice(
                salaryRangeIndex,
                1,
              );
              state.additionalInfo.details.version = event.version;
            }
          }
        }
      });

      state.status = "succeeded";
    });
    builder.addCase(fetchEmploymentAttributesOverview.pending, (state) => {
      state.attributes.status = "pending";
    });
    builder.addCase(fetchEmploymentAttributesOverview.rejected, (state) => {
      state.attributes.status = "failed";
    });
    builder.addCase(
      fetchEmploymentAttributesOverview.fulfilled,
      (state, action) => {
        employmentAttributesAdapter.setAll(
          state.attributes,
          action.payload.attributes,
        );
        state.attributes.supportedZones = action.payload.supportedZones;
        state.attributes.status = "succeeded";
      },
    );
    builder.addCase(endEmploymentAttributesSalary.pending, (state) => {
      state.attributes.status = "pending";
    });
    builder.addCase(endEmploymentAttributesSalary.rejected, (state) => {
      state.attributes.status = "failed";
    });
    builder.addCase(
      endEmploymentAttributesSalary.fulfilled,
      (state, action) => {
        state.attributesForEditing.attributes.version = action.payload.version;
        state.attributes.status = "succeeded";
      },
    );
    builder.addCase(addAccumulationDelta.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addAccumulationDelta.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addAccumulationDelta.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(delAccumulationDelta.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(delAccumulationDelta.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(delAccumulationDelta.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(
      fetchAccumulationsForContinuousEmployment.pending,
      (state) => {
        state.status = "pending";
      },
    );
    builder.addCase(
      fetchAccumulationsForContinuousEmployment.rejected,
      (state) => {
        state.status = "failed";
      },
    );
    builder.addCase(
      fetchAccumulationsForContinuousEmployment.fulfilled,
      (state) => {
        state.status = "succeeded";
      },
    );
    builder.addCase(endEmployment.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(endEmployment.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(endEmployment.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach((event) => {
          const { version, payload } = event;
          if (event.name === "empEndSet") {
            state.additionalInfo.details.endReason = payload.reason;
            state.additionalInfo.details.agreedEnd = payload.endDate;
            state.additionalInfo.details.version = version;
          }
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(fetchAccumulationsForEmployment.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchAccumulationsForEmployment.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchAccumulationsForEmployment.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(editEmpOtherAgreedInformation.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(editEmpOtherAgreedInformation.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      editEmpOtherAgreedInformation.fulfilled,
      (state, action) => {
        if (action.payload) {
          action.payload.forEach((event) => {
            const { version, payload } = event;
            if (event.name === "empOthArgInfoChg") {
              state.additionalInfo.details.otherAgreedInformation =
                payload.otherAgreedInformation;
              state.additionalInfo.details.version = version;
            }
          });
        }
        state.status = "succeeded";
      },
    );
    builder.addCase(editEmpAnnualLeavePaymentRule.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(editEmpAnnualLeavePaymentRule.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      editEmpAnnualLeavePaymentRule.fulfilled,
      (state, action) => {
        if (action.payload) {
          action.payload.forEach((event) => {
            const { version, payload } = event;
            if (event.name === "empAnnLeaPayRulChg") {
              state.additionalInfo.details.annualLeavePaymentRule =
                payload.annualLeavePaymentRule;
              state.additionalInfo.details.version = version;
            }
          });
        }
        state.status = "succeeded";
      },
    );
    builder.addCase(editEmpAnnualHolidayAccumulationRule.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(editEmpAnnualHolidayAccumulationRule.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      editEmpAnnualHolidayAccumulationRule.fulfilled,
      (state, action) => {
        const events = action.payload || [];
        events.forEach((event) => {
          if (event.name === "empAnnHolAccRulChg") {
            state.additionalInfo.details.annualHolidayAccumulationRule =
              event.payload.annualHolidayAccumulationRule;
            state.additionalInfo.details.version = event.version;
          }
        });
        state.status = "succeeded";
      },
    );

    builder.addCase(clearEmploymentAttributesSalary.pending, (state) => {
      state.attributesForEditing.status = "pending";
    });
    builder.addCase(clearEmploymentAttributesSalary.rejected, (state) => {
      state.attributesForEditing.status = "failed";
    });
    builder.addCase(
      clearEmploymentAttributesSalary.fulfilled,
      (state, action) => {
        const salaryPerDateIndex = current(
          state.attributesForEditing.attributes.salaryPerDate,
        ).findIndex(
          (salaryPerDate) => salaryPerDate.date === action.payload.payload.from,
        );

        state.attributesForEditing.attributes.salaryPerDate.splice(
          salaryPerDateIndex,
          1,
        );

        state.attributesForEditing.attributes.version = action.payload.version;
        state.attributesForEditing.status = "succeeded";
      },
    );
    builder.addCase(fetchEmploymentAttributesForEditing.pending, (state) => {
      state.attributesForEditing.status = "pending";
    });
    builder.addCase(
      fetchEmploymentAttributesForEditing.fulfilled,
      (state, action) => {
        state.attributesForEditing.attributes = action.payload;
        state.attributesForEditing.status = "succeeded";
      },
    );
    builder.addCase(applyTesSalaryTableCell.pending, (state) => {
      // console.log("applyTesSalaryTableCell.pending")
      state.attributes.status = "pending";
    });
    builder.addCase(applyTesSalaryTableCell.rejected, (state) => {
      // console.log("applyTesSalaryTableCell.failed")
      state.attributes.status = "failed";
    });
    builder.addCase(applyTesSalaryTableCell.fulfilled, (state, action) => {
      // console.log("applyTesSalaryTableCell.succeeded", action)
      handleEmpAttrSalaryFulfilled(state, action);
    });

    builder.addCase(setNewEmpAttrSalary.pending, (state) => {
      state.attributes.status = "pending";
    });
    builder.addCase(setNewEmpAttrSalary.rejected, (state) => {
      state.attributes.status = "failed";
    });
    builder.addCase(
      setNewEmpAttrSalary.fulfilled,
      handleEmpAttrSalaryFulfilled,
    );
    builder.addCase(setContinousEmploymentId, (state, action) => {
      state.additionalInfo.continuousEmploymentId =
        action.payload.continuousEmploymentId;
    });
    builder.addCase(updateEmploymentAgreedHours.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateEmploymentAgreedHours.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateEmploymentAgreedHours.fulfilled, (state, action) => {
      const highestVersion = action.payload.reduce(
        (max, event) => Math.max(max, event.version),
        0,
      );
      const { minHours, maxHours, weeksInPeriod } = action.meta.arg.agreedHours;
      state.additionalInfo.details.agreedHours = {
        minHours,
        maxHours,
        weeksInPeriod,
      };
      state.additionalInfo.details.version = highestVersion;
      state.status = "succeeded";
    });
    builder.addCase(updateEmploymentWorkTimeBankSettings.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateEmploymentWorkTimeBankSettings.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateEmploymentWorkTimeBankSettings.fulfilled,
      (state, action) => {
        if (action.payload.length > 0) {
          const highestVersion = action.payload.reduce(
            (max, event) => Math.max(max, event.version),
            0,
          );
          const timeBankSettings = action.meta.arg.settings;
          state.additionalInfo.details.timeBankSettings = timeBankSettings;
          state.additionalInfo.details.version = highestVersion;
        }
        state.status = "succeeded";
      },
    );
    builder.addCase(updateEmploymentFlexibleHoursSettings.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateEmploymentFlexibleHoursSettings.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateEmploymentFlexibleHoursSettings.fulfilled,
      (state, action) => {
        if (action.payload.length > 0) {
          const highestVersion = action.payload.reduce(
            (max, event) => Math.max(max, event.version),
            0,
          );
          const flexibleHoursSettings = action.meta.arg.settings;
          state.additionalInfo.details.flexibleHoursSettings =
            flexibleHoursSettings;
          state.additionalInfo.details.version = highestVersion;
        }
        state.status = "succeeded";
      },
    );
    builder.addCase(fetchEmploymentTargetedShiftSpec.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchEmploymentTargetedShiftSpec.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchEmploymentTargetedShiftSpec.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(setShiftTargetingRules.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(setShiftTargetingRules.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(setShiftTargetingRules.fulfilled, (state, action) => {
      state.status = "succeeded";
      if (state.additionalInfo) {
        // Optimistic update: keep targetingRules count in sync for summary display.
        // Names are not available here so we map with empty strings — length is what matters.
        state.additionalInfo.targetedShiftSpec = {
          version:
            action.payload[0]?.version ??
            state.additionalInfo.targetedShiftSpec?.version ??
            0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          targetingRules: action.meta.arg.rules.map((r: any) => ({
            custCompany: { id: r.custCompanyId, name: "" },
            custOrgs: (r.custOrgs ?? []).map((id: string) => ({
              id,
              name: "",
            })),
            workLocs: (r.workLocs ?? []).map((id: string) => ({
              id,
              name: "",
            })),
            workTitles: r.workTitles ?? [],
          })),
          salaries: normalizeSalaryRows(
            state.additionalInfo.targetedShiftSpec?.salaries,
          ),
          variationValidationAssignmentId:
            state.additionalInfo.targetedShiftSpec
              ?.variationValidationAssignmentId ?? null,
        };
      }
    });
    builder.addCase(disableTargetedShifts.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(disableTargetedShifts.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(disableTargetedShifts.fulfilled, (state) => {
      state.status = "succeeded";
      state.additionalInfo.targetedShiftSpec = undefined;
    });
    builder.addCase(saveTargetedShiftSalaryRow.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(saveTargetedShiftSalaryRow.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(saveTargetedShiftSalaryRow.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(removeTargetedShiftSalaryRow.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeTargetedShiftSalaryRow.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeTargetedShiftSalaryRow.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchFringeBenefitTypes.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchFringeBenefitTypes.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchFringeBenefitTypes.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(saveFringeBenefits.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(saveFringeBenefits.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(saveFringeBenefits.fulfilled, (state, action) => {
      if (action.payload && action.payload.length > 0) {
        state.additionalInfo.details.version = action.payload[0].version;
        state.additionalInfo.details.fringeBenefits =
          action.payload[0].payload.fringeBenefits;
      }
      state.status = "succeeded";
    });
    builder.addMatcher(
      isAnyOf(
        addPeriodStartSetting.pending,
        editPeriodStartSetting.pending,
        removePeriodStartSetting.pending,
      ),
      (state) => {
        state.status = "pending";
      },
    );
    builder.addMatcher(
      isAnyOf(
        addPeriodStartSetting.rejected,
        editPeriodStartSetting.rejected,
        removePeriodStartSetting.rejected,
      ),
      (state) => {
        state.status = "failed";
      },
    );
    builder.addMatcher(
      isAnyOf(
        addPeriodStartSetting.fulfilled,
        editPeriodStartSetting.fulfilled,
        removePeriodStartSetting.fulfilled,
      ),
      (state, action) => {
        const employment = state.additionalInfo;
        if (
          employment.employmentId === action.meta.arg.employmentId &&
          employment.details
        ) {
          const baseline = periodStartBaseline(employment.details);
          action.payload
            .filter((event) => event.id === employment.employmentId)
            .sort((a, b) => a.version - b.version)
            .forEach((event) => {
              if (event.version <= employment.details.version) return;
              let settings = employment.details.periodStartSettings ?? [];
              const { setting } = event.payload;
              switch (event.name) {
                case "empPerSettingAdd":
                  settings = [
                    ...settings.filter((row) => row.from !== setting.from),
                    setting,
                  ];
                  break;
                case "empPerSettingRem":
                  settings = settings.filter(
                    (row) => row.from !== setting.from,
                  );
                  break;
                default:
                  return;
              }
              employment.details.periodStartBaseline = baseline;
              employment.details.periodStartSettings = settings.sort((a, b) =>
                a.from.localeCompare(b.from),
              );
              employment.details.periodStartDate = resolvePeriodStartDate(
                baseline,
                settings,
                employmentDateInHelsinki(),
              );
              employment.details.version = event.version;
            });
        }
        state.status = "succeeded";
      },
    );
  },
});

export const {
  selectAll: selectAllEmploymentAttributes,
  selectById: selectEmploymentAttributesById,
} = employmentAttributesAdapter.getSelectors(
  (state: RootState) => state.employment.attributes,
);

export const selectSupportedZones = (state: RootState) =>
  state.employment.attributes.supportedZones;

export const { resetEmploymentAdditionalInfo, updateTargetedShiftSpec } =
  employmentSlice.actions;

export const employmentReducer = employmentSlice.reducer;
